import { useState } from 'react';
import {
  measureLine,
  measureTrendLine,
  scoresWithin,
  type Band,
  type Measure,
  type MeasureScore,
} from '@ehr/shared';
import { RailFoldBtn, type RailFold } from './RailFold.js';

/**
 * ehr#437 (PROGRESS_NOTE_10-14-V81 Measures, measurement-based care): the client's scored measures
 * in the note's rail (latest score, band, the change since last time, a sparkline, and a sentence
 * for the note), and the History panel's Measures tab (a graph over a range, with the bands).
 */

// V81 BAND_COLOR, by the band's severity (and its name, for screeners that name it).
const SEVERITY_COLOR: Record<string, string> = {
  low: '#cfeee0',
  minimal: '#cfeee0',
  mild: '#e3edc4',
  mod: '#f6e3ad',
  moderate: '#f6e3ad',
  'moderately severe': '#f5c9a6',
  high: '#ec9d96',
  severe: '#ec9d96',
};
const bandColor = (b: { severity?: string | null; label?: string | null }) =>
  SEVERITY_COLOR[(b.label ?? '').toLowerCase()] ?? SEVERITY_COLOR[b.severity ?? ''] ?? '#dde8e3';
const mdy = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${m}/${d}/${y}` : iso;
};

/** One series, 2px line, the last point marked; each point names its date and score. */
function Spark({ m }: { m: Measure }) {
  const W = 96;
  const H = 30;
  const P = 4;
  const s = m.scores;
  const n = s.length;
  const xs = (i: number) => (n === 1 ? W / 2 : P + (i * (W - 2 * P)) / (n - 1));
  const ys = (v: number) => P + (1 - v / (m.max || 1)) * (H - 2 * P);
  return (
    <svg
      className="pn-spark"
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      role="img"
      aria-label={`${m.key} trend: ${s.map((p) => p.score).join(', ')}`}
    >
      <polyline
        points={s.map((p, i) => `${xs(i).toFixed(1)},${ys(p.score).toFixed(1)}`).join(' ')}
        fill="none"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {s.map((p, i) => (
        <circle
          key={p.id + p.date}
          cx={xs(i)}
          cy={ys(p.score)}
          r={i === n - 1 ? 3 : 1.6}
          className={i === n - 1 ? 'last' : ''}
        >
          <title>
            {mdy(p.date)}: {p.score}
          </title>
        </circle>
      ))}
    </svg>
  );
}

/** The rail card: each measure's latest score, band and change, with "+ Add to <box>". */
export function MeasuresCard({
  measures,
  boxLabel,
  locked,
  onAdd,
  onOpen,
  fold,
}: {
  /** ehr#437 reopen (V81 rail): the card folds to its header. */
  fold?: RailFold;
  measures: Measure[];
  boxLabel: string;
  locked: boolean;
  /** Adds the sentence to the box; says what happened. */
  onAdd: (line: string) => string;
  onOpen: (key: string) => void;
}) {
  const [msg, setMsg] = useState('');
  if (!measures.length) return null;
  if (fold && !fold.open)
    return (
      <section className="pn-card pn-meas pn-rail-shut" aria-label="Measures">
        <div className="pn-rail-h">
          <h3>Measures</h3>
          <span className="pn-sp" />
          <RailFoldBtn fold={fold} title="Measures" />
        </div>
      </section>
    );
  return (
    <section className="pn-card pn-meas" aria-label="Measures">
      <div className="pn-rail-h">
        <h3>Measures</h3>
        <span className="pn-sp" />
        {fold && <RailFoldBtn fold={fold} title="Measures" />}
      </div>
      {measures.map((m) => {
        const last = m.scores[m.scores.length - 1]!;
        const prev = m.scores[m.scores.length - 2];
        const delta = prev ? last.score - prev.score : 0;
        return (
          <div key={m.key} className="pn-meas-row">
            <button
              type="button"
              className="pn-meas-n"
              aria-label={`${m.key} history`}
              onClick={() => onOpen(m.key)}
            >
              <b>{m.key}</b>
              <span className="pn-meas-v">{last.score}</span>
              {last.band && (
                <span
                  className="pn-meas-b"
                  style={{ background: bandColor(last), color: '#0d1f18' }}
                >
                  {last.band}
                </span>
              )}
              {prev && (
                <span className={`pn-meas-d${delta < 0 ? ' down' : delta > 0 ? ' up' : ''}`}>
                  {delta === 0 ? 'no change' : `${delta > 0 ? '+' : ''}${delta}`}
                </span>
              )}
            </button>
            <Spark m={m} />
            {!locked && boxLabel && (
              <button
                type="button"
                className="pn-link pn-meas-add"
                onClick={() => setMsg(onAdd(measureLine(m)))}
              >
                + Add to {boxLabel}
              </button>
            )}
            {last.alert && <div className="pn-meas-alert">{last.alert}</div>}
          </div>
        );
      })}
      {msg && (
        <p className="pn-rail-msg" role="status">
          {msg}
        </p>
      )}
    </section>
  );
}

const RANGES: [string, number][] = [
  ['3 months', 3],
  ['6 months', 6],
  ['12 months', 12],
  ['All', 0],
];

/** The graph: the bands shaded behind the scores, dates under them (V81 measureGraphSVG). */
function Graph({ m, s }: { m: Measure; s: MeasureScore[] }) {
  const W = 340;
  const H = 190;
  const L = 30;
  const R = 16;
  const T = 10;
  const B = 26;
  const n = s.length;
  const t0 = n ? Date.parse(s[0]!.date) : 0;
  const t1 = n ? Date.parse(s[n - 1]!.date) : 1;
  const max = m.max || 1;
  const xs = (p: MeasureScore) =>
    n === 1 ? (L + W - R) / 2 : L + ((Date.parse(p.date) - t0) / (t1 - t0 || 1)) * (W - L - R);
  const ys = (v: number) => T + (1 - v / max) * (H - T - B);
  const bands: Band[] = m.bands;
  return (
    <svg
      className="pn-mgraph"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`${m.key} scores over time: ${s.map((p) => p.score).join(', ')}`}
    >
      {bands.map((b, i) => {
        // a band reaches up to where the next one starts, so the shading has no gaps
        const top = Math.min(i < bands.length - 1 ? bands[i + 1]!.min : max, max);
        return (
          <g key={`${b.min}-${b.max}`}>
            <rect
              x={L}
              y={ys(top).toFixed(1)}
              width={W - L - R}
              height={(ys(b.min) - ys(top)).toFixed(1)}
              fill={bandColor(b)}
              fillOpacity="0.45"
            />
            <text x={L + 4} y={(ys(b.min) - 4).toFixed(1)} className="pn-mg-band">
              {b.label}
            </text>
          </g>
        );
      })}
      {[0, Math.round(max / 2), max].map((v) => (
        <text key={v} x={L - 6} y={(ys(v) + 4).toFixed(1)} textAnchor="end" className="pn-mg-ax">
          {v}
        </text>
      ))}
      {s.map((p, i) =>
        n <= 6 || i % Math.ceil(n / 6) === 0 || i === n - 1 ? (
          <text
            key={`d${p.id}${p.date}`}
            x={xs(p).toFixed(1)}
            y={H - 8}
            textAnchor="middle"
            className="pn-mg-ax"
          >
            {mdy(p.date).slice(0, 5)}
          </text>
        ) : null,
      )}
      <polyline
        points={s.map((p) => `${xs(p).toFixed(1)},${ys(p.score).toFixed(1)}`).join(' ')}
        fill="none"
        strokeWidth="2"
        strokeLinejoin="round"
        className="pn-mg-line"
      />
      {s.map((p) => (
        <circle
          key={`p${p.id}${p.date}`}
          cx={xs(p).toFixed(1)}
          cy={ys(p.score).toFixed(1)}
          r="4"
          className={p.alert ? 'pn-mg-flag' : 'pn-mg-pt'}
        >
          <title>
            {mdy(p.date)}: {p.score}
            {p.band ? ` (${p.band})` : ''}
            {p.alert ? `, ${p.alert}` : ''}
          </title>
        </circle>
      ))}
    </svg>
  );
}

/** The History panel's Measures tab: one measure at a time over a range, "+ Add trend". */
export function MeasureHistory({
  measures,
  initialKey,
  boxLabel,
  locked,
  onAdd,
}: {
  measures: Measure[];
  initialKey?: string;
  boxLabel: string;
  locked: boolean;
  onAdd: (line: string) => string;
}) {
  const [key, setKey] = useState(initialKey ?? measures[0]?.key ?? '');
  const [months, setMonths] = useState(0);
  const [msg, setMsg] = useState('');
  const m = measures.find((x) => x.key === key) ?? measures[0];
  if (!m) return <p className="pn-hist-empty">No scored measures yet.</p>;
  const s = scoresWithin(m, months);
  return (
    <div className="pn-mh">
      <div className="pn-seg" role="group" aria-label="Measure">
        {measures.map((x) => (
          <button
            key={x.key}
            type="button"
            aria-pressed={x.key === m.key}
            onClick={() => setKey(x.key)}
          >
            {x.key}
          </button>
        ))}
      </div>
      <div className="pn-seg" role="group" aria-label="Time range">
        {RANGES.map(([label, mo]) => (
          <button
            key={label}
            type="button"
            aria-pressed={months === mo}
            onClick={() => setMonths(mo)}
          >
            {label}
          </button>
        ))}
      </div>
      <Graph m={m} s={s} />
      <ul className="pn-mh-list">
        {[...s].reverse().map((p) => (
          <li key={`${p.id}${p.date}`}>
            <span>{mdy(p.date)}</span>
            <b>{p.score}</b>
            <span>{p.band ?? ''}</span>
            <span className="pn-mh-src">{p.source === 'portal' ? 'Portal' : 'In the EHR'}</span>
          </li>
        ))}
      </ul>
      {!locked && boxLabel && (
        <button
          type="button"
          className="pn-link pn-meas-add"
          onClick={() => setMsg(onAdd(measureTrendLine(m, s)))}
        >
          + Add trend to {boxLabel}
        </button>
      )}
      {msg && (
        <p className="pn-rail-msg" role="status">
          {msg}
        </p>
      )}
    </div>
  );
}
